import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import influencerAction from '../../../actions/influencer.actions';
import FormsyForm from 'epic-diesel-form/src/components/external/FormsyForm';
import lodashClonedeep from 'lodash.clonedeep';
import {Message} from 'epic-diesel-common';
import {SocialChannelSection, MainSocialChannelSection} from '../../../components';
import {Affiliate, SocialChannel} from '../../../../shared/types';
import {selectDisconnectedPlatform, selectLastSocialUpdate} from '../../../selectors';
import Divider from '@material-ui/core/Divider';

export interface SocialSectionProps {
    affiliate: Affiliate;
    messages: Record<string, string>;
    configState: Record<string, any>;
}

export interface SocialSectionState {
    oauthForm: {
        showError: boolean;
        channels: SocialChannel[];
        mainChannelPlatform: string;
    };
}

const SocialSection = ({affiliate, messages, configState}: SocialSectionProps) => {
    const dispatch = useDispatch();
    const [state, setState] = useState<SocialSectionState>(() => ({
        oauthForm: {
            showError: false,
            channels: [],
            mainChannelPlatform: null
        }
    }));

    const influencerState = useSelector(state => ({
        disconnectedPlatform: selectDisconnectedPlatform(state),
        lastSocialUpdate: selectLastSocialUpdate(state)
    }));

    const {disconnectedPlatform, lastSocialUpdate} = influencerState || {};

    const updateOAuthForm = formData => {
        const newState = {...state};
        Object.assign(newState.oauthForm, formData);
        setState(newState);
    };

    const updateOAuthFormErrors = (hasError = false) => {
        const {oauthForm} = state;

        let error = hasError;

        if (!oauthForm.mainChannelPlatform) {
            error = true;
        }

        updateOAuthForm({showError: error});
        return error;
    };

    const refreshAuthForm = () => {
        const {socialChannels = []} = affiliate || {};
        const channel = socialChannels.find(ch => ch.mainChannel === true);
        updateOAuthForm({
            channels: lodashClonedeep(socialChannels),
            mainChannelPlatform: channel ? channel.platform : null
        });
    };

    useEffect(() => {
        refreshAuthForm();
    }, []);

    // Refresh Social Form
    useEffect(() => {
        if (disconnectedPlatform) {
            refreshAuthForm();
            dispatch(
                influencerAction.resetDisconnectedPlatform({
                    platform: disconnectedPlatform
                })
            );
        } else if (lastSocialUpdate) {
            refreshAuthForm();
        }
    }, [disconnectedPlatform, lastSocialUpdate]);

    const disconnectSocialChannel = (channel: SocialChannel, severConnection?: boolean) => {
        dispatch(
            influencerAction.disconnectSocialChannel({
                channel,
                severConnection
            })
        );
    };

    const getSocialChannels = () => {
        dispatch(influencerAction.getSocialChannels());
    };

    return (
        <>
            <div key="MainSocialChannel">
                <FormsyForm>
                    <MainSocialChannelSection
                        messages={messages}
                        mainChannelPlatform={state.oauthForm.mainChannelPlatform}
                        configState={configState}
                        channels={state.oauthForm.channels}
                        trigger={(formKey, platform) => {
                            dispatch(influencerAction.updateMainChannel({platform}));
                        }}
                        showError={state.oauthForm.showError}
                    />
                </FormsyForm>
            </div>

            <Divider style={{margin: '60px 0'}} />

            <div key="SocialSection">
                <FormsyForm>
                    <h2>
                        <Message code="epic.affiliate.social.header" />
                    </h2>
                    <SocialChannelSection
                        messages={messages}
                        configState={configState}
                        affiliate={affiliate}
                        channels={state.oauthForm.channels}
                        updateForm={updateOAuthForm}
                        mainChannelPlatform={state.oauthForm.mainChannelPlatform}
                        updateFormErrors={updateOAuthFormErrors}
                        getSocialChannels={getSocialChannels}
                        disconnectSocialChannel={disconnectSocialChannel}
                    />
                </FormsyForm>
            </div>
        </>
    );
};

export default SocialSection;
